// apps/app/src/components/layout/nav-config.ts
//
// The main navigation, shared by the desktop rail and the mobile drawer.
// Labels are keys in the common namespace, translated where they render.

import {
  Building2,
  CalendarDays,
  ChartColumn,
  KanbanSquare,
  Layers,
  TicketPercent,
  BookOpen,
  Images,
  LayoutDashboard,
  Mail,
  MonitorPlay,
  Package,
  PanelsTopLeft,
  PhoneCall,
  Plug,
  ScanFace,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { keys } from '@/lib/query-client';
import type { Capability } from '@/lib/permissions';

export type BadgeKey = 'leads' | 'requests' | 'verification';

export interface NavItem {
  to: string;
  /** Key in the common namespace, e.g. 'nav.items.patients'. */
  labelKey: string;
  icon: LucideIcon;
  capability?: Capability;
  /** Live count shown as a right-aligned pill. */
  badge?: BadgeKey;
  /** Matches only the exact path, for a section's index page. */
  end?: boolean;
  /**
   * The page's first query, fetched on hover. The pointer reaches a link a few
   * hundred milliseconds before the click, so the page usually opens with its
   * data already in the cache. Key and path must match the page's own query.
   */
  prefetch?: { queryKey: readonly unknown[]; path: string };
}

export interface NavGroup {
  /** Key in the common namespace, e.g. 'nav.groups.main'. */
  titleKey: string;
  items: NavItem[];
}

/**
 * The operator console. Shown instead of the clinic groups: platform staff
 * belong to no clinic, and every clinic page would only 403 for them.
 */
export const PLATFORM_GROUPS: NavGroup[] = [
  {
    titleKey: 'nav.groups.platform',
    items: [
      {
        to: '/console',
        labelKey: 'nav.items.overview',
        icon: LayoutDashboard,
        end: true,
        prefetch: { queryKey: keys.platformOverview, path: '/platform/overview' },
      },
      {
        to: '/console/clinics',
        labelKey: 'nav.items.clinics',
        icon: Building2,
        // The directory's unfiltered key — what it asks for when opened from here.
        prefetch: {
          queryKey: keys.platformClinics({ q: '', state: '', verification: '' }),
          path: '/platform/clinics',
        },
      },
      {
        to: '/console/plans',
        labelKey: 'nav.items.plans',
        icon: Layers,
        prefetch: { queryKey: keys.platformPlans, path: '/platform/plans' },
      },
      {
        to: '/console/coupons',
        labelKey: 'nav.items.coupons',
        icon: TicketPercent,
        prefetch: { queryKey: keys.platformCoupons, path: '/platform/coupons' },
      },
      {
        to: '/console/treatment-library',
        labelKey: 'nav.items.treatmentLibrary',
        icon: BookOpen,
        prefetch: { queryKey: keys.platformTreatmentLibrary, path: '/platform/treatment-library' },
      },
      {
        to: '/console/media',
        labelKey: 'nav.items.media',
        icon: Images,
      },
      {
        to: '/console/emails',
        labelKey: 'nav.items.emails',
        icon: Mail,
        // The log's unfiltered key — what the page asks for when opened from here.
        prefetch: {
          queryKey: keys.platformEmails({ q: '', status: '', template: '' }),
          path: '/platform/emails',
        },
      },
      {
        to: '/settings/clinics',
        labelKey: 'nav.items.verification',
        icon: ShieldCheck,
        badge: 'verification',
        prefetch: { queryKey: keys.verificationQueue, path: '/verification/queue' },
      },
      {
        to: '/settings/integrations',
        labelKey: 'nav.items.integrations',
        icon: Plug,
        prefetch: { queryKey: keys.platformSettings, path: '/platform/settings' },
      },
      {
        to: '/settings/staff',
        labelKey: 'nav.items.staff',
        icon: UserCog,
        prefetch: { queryKey: keys.platformStaff, path: '/platform/staff' },
      },
    ],
  },
];

export const CLINIC_GROUPS: NavGroup[] = [
  {
    titleKey: 'nav.groups.main',
    items: [
      { to: '/', labelKey: 'nav.items.dashboard', icon: LayoutDashboard, end: true },
      { to: '/scans', labelKey: 'nav.items.scans', icon: ScanFace },
      { to: '/patients', labelKey: 'nav.items.patients', icon: Users },
      // Every enquiry from first contact to booked — the board staff work from.
      { to: '/leads', labelKey: 'nav.items.pipeline', icon: KanbanSquare, badge: 'leads' },
      { to: '/bookings', labelKey: 'nav.items.bookings', icon: CalendarDays, end: true },
      // The widget's booking-form submissions, waiting for a call back.
      { to: '/bookings/requests', labelKey: 'nav.items.callbacks', icon: PhoneCall, badge: 'requests' },
    ],
  },
  {
    titleKey: 'nav.groups.catalogue',
    items: [
      { to: '/treatments', labelKey: 'nav.items.treatments', icon: Sparkles, capability: 'catalogue.write' },
      { to: '/products', labelKey: 'nav.items.products', icon: Package, capability: 'catalogue.write' },
      // Every picture the clinic has uploaded, picked again from any image field.
      { to: '/media', labelKey: 'nav.items.media', icon: Images, capability: 'widget.configure' },
    ],
  },
  {
    titleKey: 'nav.groups.grow',
    items: [
      { to: '/analytics', labelKey: 'nav.items.analytics', icon: ChartColumn, capability: 'analytics.read' },
      { to: '/widget', labelKey: 'nav.items.widget', icon: PanelsTopLeft, capability: 'widget.configure', end: true },
      { to: '/widget/test', labelKey: 'nav.items.testWidget', icon: MonitorPlay, capability: 'widget.configure' },
      { to: '/orders', labelKey: 'nav.items.orders', icon: ShoppingBag },
    ],
  },
];
